import axios from 'axios';
import { ref, readonly } from 'vue';
import { useGetters } from './getters';

// Shared reactive state
const globalSettings = ref(null);
const isLoadingSettings = ref(false);

/**
 * Get CSP nonce for script injection
 */
const getCSPNonce = () => {
    const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (cspMeta) {
        const cspContent = cspMeta.getAttribute('content');
        const nonceMatch = cspContent.match(/script-src[^;]*'nonce-([^']+)'/);
        if (nonceMatch) {
            return nonceMatch[1];
        }
    }
    
    const existingScript = document.querySelector('script[nonce]');
    if (existingScript) {
        return existingScript.getAttribute('nonce');
    }
    
    if (window.__webpack_nonce__) {
        return window.__webpack_nonce__;
    }
    
    return null;
};

/**
 * Inject a script into the DOM
 */
const pluginScript = (script) => {
    let scriptElem;
    if (script.type === "inline" && script.content_type === "html") {
        try {
            if (script.placement === "head") {
                document.head.insertAdjacentHTML('beforeend', script.content);
            } else if (script.placement === "body_end") {
                document.body.insertAdjacentHTML('beforeend', script.content);
            } else if (script.placement === "body_start") {
                document.body.insertAdjacentHTML('afterbegin', script.content);
            }
        } catch (error) {
            console.error('pluginScript: Error injecting inline HTML content:', error, script.content);
        }
    } else if (script.type === "inline") {
        scriptElem = document.createElement("script");
        try {
            scriptElem.textContent = script.content;
        } catch (error) {
            console.error('pluginScript: Error setting inline script content:', error, script.content);
            return;
        }
    } else if (script.type === "external") {
        scriptElem = document.createElement("script");
        scriptElem.src = script.content;
    } else if (script.type === "noscript") {
        scriptElem = document.createElement("noscript");
        try {
            if (script.content_type === "html") {
                scriptElem.innerHTML = script.content;
            } else {
                scriptElem.textContent = script.content;
            }
        } catch (error) {
            console.error('pluginScript: Error setting noscript content:', error, script.content);
            return;
        }
    }

    if (scriptElem && (script.type === "inline" || script.type === "external")) {
        const nonce = getCSPNonce();
        if (nonce) {
            scriptElem.setAttribute('nonce', nonce);
        }
    }

    if (scriptElem && script.attrs) {
        const attrs = script.attrs.split(",");
        attrs.forEach((attr) => {
            attr = attr.split("=");
            let value = "";
            if (attr.length > 1) {
                value = attr[1].trim();

                if (['"', "'"].includes(value.charAt(0))) {
                    value = value.substring(1);
                }

                const lastChar = value.charAt(value.length - 1);
                if (['"', "'"].includes(lastChar)) {
                    value = value.substring(0, value.length - 1);
                }
            }
            scriptElem.setAttribute(attr[0].trim(), value);
        });
    }

    try {
        if (script.placement === "head") {
            document.head.appendChild(scriptElem);
        } else if (script.placement === "body_end") {
            document.body.appendChild(scriptElem);
        } else if (script.placement === "body_start") {
            const firstChild = document.body.firstChild;
            if (firstChild) {
                firstChild.parentNode.insertBefore(scriptElem, firstChild);
            } else {
                document.body.appendChild(scriptElem);
            }
        }
    } catch (error) {
        console.error('pluginScript: Error appending script to DOM:', error, script);
    }
};

/**
 * Composable for app-level functionality using Composition API
 * @returns {Object} App management methods and state
 */
export function useApp() {
    const { isLoggedIn } = useGetters();
    /**
     * Fetch and store global settings
     */
    const getGlobalSettings = async () => {
        isLoadingSettings.value = true;
        try {
            const response = await axios.get(
                process.env.VUE_APP_API_BASE + "auth/getGlobalSettings",
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + localStorage.getItem("token"),
                    },
                }
            );

            if (response.data.success) {
                const data = response.data.data;
                globalSettings.value = data.settings;
                
                localStorage.setItem('site_settings', JSON.stringify(data.settings));
                localStorage.setItem('company_name', data.settings.company_name);
                localStorage.setItem('company_logo', data.settings.logo);
                localStorage.setItem('meta_title_brand', data.settings.meta_title_brand);
                localStorage.setItem('meta_title', data.settings.meta_title);
                localStorage.setItem('meta_description', data.settings.meta_description);
                localStorage.setItem('fl_settings', JSON.stringify(data.fl_settings));
                localStorage.setItem('fl_unit_types', JSON.stringify(data.fl_unit_types));
                localStorage.setItem('fl_units_of_measure', JSON.stringify(data.fl_units_of_measure));
                localStorage.setItem('fl_available_dates', JSON.stringify(data.fl_available_dates));
                localStorage.setItem('fl_min_date', data.fl_min_date);
                localStorage.setItem('fl_max_date', data.fl_max_date);
                localStorage.setItem('fl_disabled_dates', JSON.stringify(data.fl_disabled_dates));
            }
        } catch (error) {
            console.log(error);
        } finally {
            isLoadingSettings.value = false;
        }
    };

    /**
     * Inject scripts from API into the page
     */
    const injectScripts = async () => {
        let slug = window.location.pathname;
        if (slug.charAt(0) === '/') {
            slug = slug.substring(1);
        }

        try {
            const response = await axios.post(
                process.env.VUE_APP_API_BASE + "auth/scripts",
                { slug: slug },
                {
                    headers: {
                        "Content-type": "application/json",
                        "Access-Control-Allow-Origin": "*",
                    },
                }
            );

            response.data.scripts.forEach((script) => {
                let scriptAttrs = script.other_attributes;

                if (script.src) {
                    script.src = script.src.trim();
                }
                if (script.src) {
                    pluginScript({
                        type: "external",
                        content: script.src,
                        placement: script.placement,
                        attrs: scriptAttrs,
                    });
                    scriptAttrs = "";
                }

                if (script.content) {
                    script.content = script.content.trim();
                }
                if (script.content) {
                    let type = script.type;
                    if (script.type !== "noscript") {
                        type = "inline";
                    }

                    pluginScript({
                        type,
                        content: script.content,
                        content_type: script.content_type,
                        placement: script.placement,
                        attrs: scriptAttrs,
                    });
                }
            });
        } catch (error) {
            console.log(error);
        }
    };

    /**
     * Update program visibility based on login status
     */
    const updateProgramVisibility = async () => {
        try {
            const response = await axios.post(
                process.env.VUE_APP_API_BASE + "auth/getProgramStatuses",
                {},
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + localStorage.getItem("token"),
                    },
                }
            );

            if (response.data.success) {
                const programClasses = response.data.data.programs;
                const specials = response.data.data.specials;
                
                setTimeout(() => {
                    programClasses.forEach(program => {
                        const shouldDisplay = isLoggedIn.value ? program.status === 1 : program.require_login !== 1;
                        const displayClass = shouldDisplay ? 'd-block' : 'd-none';
                        const oppositeClass = shouldDisplay ? 'd-none' : 'd-block';
        
                        const elements = document.querySelectorAll('.' + program.css_class);
                        elements.forEach(element => {
                            element.classList.remove(oppositeClass);
                            element.classList.add(displayClass);
                        });
                    });

                    if (specials.status) {
                        let elements = document.querySelectorAll('.' + specials.specials_class);
                        if (elements.length > 0) {
                            elements.forEach((element) => {
                                element.classList.remove('d-none');
                            });
                        }
        
                        elements = document.querySelectorAll('.' + specials.not_specials_class);
                        if (elements.length > 0) {
                            elements.forEach((element) => {
                                element.classList.add('d-none');
                            });
                        }
                    } else {
                        let elements = document.querySelectorAll('.' + specials.not_specials_class);
                        if (elements.length > 0) {
                            elements.forEach((element) => {
                                element.classList.remove('d-none');
                            });
                        }
        
                        elements = document.querySelectorAll('.' + specials.specials_class);
                        if (elements.length > 0) {
                            elements.forEach((element) => {
                                element.classList.add('d-none');
                            });
                        }
                    }
                }, 1000);
            }
        } catch (error) {
            console.log(error);
        }
    };

    return {
        // State
        globalSettings: readonly(globalSettings),
        isLoadingSettings: readonly(isLoadingSettings),
        // Methods
        getGlobalSettings,
        injectScripts,
        updateProgramVisibility,
        pluginScript: (script) => pluginScript(script),
        getCSPNonce
    };
}

// Legacy mixin export for backward compatibility
export default {
    computed: {
        isLoggedIn() {
            return this.$store.getters.isLoggedIn;
        }
    },
    methods: {
        getGlobalSettings() {
            const { getGlobalSettings } = useApp();
            return getGlobalSettings();
        },
        injectScripts() {
            const { injectScripts } = useApp();
            return injectScripts();
        },
        pluginScript(script) {
            pluginScript(script);
        },
        getCSPNonce() {
            return getCSPNonce();
        },
        updateProgramVisibility() {
            const { updateProgramVisibility } = useApp();
            return updateProgramVisibility(this.isLoggedIn);
        },
    },
};

